import mqtt from "mqtt"
import { notification } from "components/ui"
import { DfAvatarLogin } from "components/ui/Icons"
import useStore from "components/ui/Context"
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react"
import LivechatRepository from "api/repository/LivechatRepository"
import { maskPhoneNumber } from "utils/helper"

// Maximum number of messages to keep in DOM
const MAX_MESSAGES = 200
const MESSAGES_TO_REMOVE = 50 // Remove this many oldest messages when limit is reached

// Separate Message component outside to prevent re-renders
const Message = React.memo(
  ({ item, isNew }) => {
    const [isVisible, setIsVisible] = React.useState(!isNew)
    const [imgError, setImgError] = React.useState(false)

    React.useEffect(() => {
      if (isNew) {
        // Small delay to trigger animation
        const timer = setTimeout(() => setIsVisible(true), 10)
        return () => clearTimeout(timer)
      }
    }, [isNew])

    return (
      <div
        className={`max-w-full py-2 px-4 transition-all duration-300 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="flex items-start gap-3">
          {item?.avatar && !imgError ? (
            <img
              className="h-6 w-6 rounded-full border border-[#FFFFFFE0] overflow-hidden object-cover"
              src={item?.avatar}
              alt={item?.user_name}
              onError={() => setImgError(true)}
            />
          ) : (
            DfAvatarLogin
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm leading-relaxed">
              <span className="text-white font-semibold">
                {maskPhoneNumber(item?.user_name) || "User" + item?.user_id}
              </span>
              <span className="text-gray-300 w-full break-words"> {item?.message || ""}</span>
            </p>
          </div>
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if these fields change
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.message === nextProps.item.message &&
      prevProps.item.user_name === nextProps.item.user_name &&
      prevProps.item.avatar === nextProps.item.avatar &&
      prevProps.isNew === nextProps.isNew
    )
  }
)
Message.displayName = "Message"

// Separate MessageList component to isolate from input re-renders
const MessageList = React.memo(
  ({ messages, messageDivRef, newMessageIds }) => {
    return (
      <div
        className="relative bg-[#1A1A1A] w-full h-[calc(100%-72px-64px)] md:h-[calc(100%-72px-64px)] overflow-y-scroll scroll-smooth"
        ref={messageDivRef}
      >
        {messages?.length > 0 &&
          messages.map((item) => <Message item={item} key={item.id} isNew={!!newMessageIds[item.id]} />)}
        {messages?.length <= 0 && (
          <div className="h-full flex flex-col justify-center items-center text-xs text-[#AFAFAF]">
            {iconNoChat}
            <p className="font-semibold">Hiện chưa có bình luận</p>
            <p>Hãy là người đầu tiên bình luận!</p>
          </div>
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if messages array length or loading state changes
    return (
      prevProps.messages.length === nextProps.messages.length &&
      prevProps.loading === nextProps.loading &&
      prevProps.messages[prevProps.messages.length - 1]?.id === nextProps.messages[nextProps.messages.length - 1]?.id
    )
  }
)
MessageList.displayName = "MessageList"

// Separate ChatInput component to isolate text state changes
const ChatInput = React.memo(
  ({ text, onTextChange, onSubmit, isLogin, isAllowChat, statusChat, errorMessage, toggleModalLogin }) => {
    return (
      <form onSubmit={onSubmit}>
        <div className="absolute left-0 flex bottom-0 w-full justify-center text-[#AFAFAF] gap-2 rounded-t-2xl px-4 p-4 text-xs items-center min-h-[72px] text-center">
          {isLogin ? (
            <>
              {!isAllowChat && <>Tính năng bình luận sẽ được mở sau khi phát sóng</>}
              {isAllowChat && (
                <>
                  {statusChat === 1 && (
                    <>
                      <input
                        value={text}
                        onChange={onTextChange}
                        type="text"
                        className="py-3 px-4 bg-[#232323] grow placeholder:text-[#FFFFFF99] placeholder:text-xs rounded-full"
                        disabled={!isLogin}
                        placeholder="Trò chuyện với mọi người"
                        maxLength={150}
                      />
                      <div
                        className={`${isLogin ? "cursor-pointer text-primary" : "cursor-not-allowed text-secondary2"}`}
                        onClick={onSubmit}
                      >
                        {iconSend}
                      </div>
                    </>
                  )}
                  {statusChat === 2 && <>{errorMessage}</>}
                  {statusChat === 3 && <>Cuộc trò chuyện này đã kết thúc</>}
                </>
              )}
            </>
          ) : (
            <div
              className="w-full text-[#FAFAF9] font-medium text-base text-center py-[10px] rounded-xl cursor-pointer bg-[#2E2E2E] border border-[#3A3A3A]"
              onClick={toggleModalLogin}
            >
              Đăng nhập để trò chuyện
            </div>
          )}
        </div>
      </form>
    )
  }
)
ChatInput.displayName = "ChatInput"

function BoxLiveChat({ dataVideo }) {
  const [isShowChat, setIsShowChat] = useState(true)
  const [text, setText] = useState("")
  const { userInfo, toggleModalLogin, setModalBan, logOut } = useStore()
  const [isLogin, setIsLogin] = useState(false)
  const [messages, setMessages] = useState([])
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const [loading, setLoading] = useState(false)
  const [statusChat, setStatusChat] = useState(1)
  const [isAllowChat, setIsAllowChat] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [videoHeight, setVideoHeight] = useState(450)
  const [isMobile, setIsMobile] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  let ws = useRef(null)
  let timeInterval = useRef(null)
  let timeEventTimeout = useRef(null)
  const messageDivRef = useRef(null)
  const lastMessageCountRef = useRef(0)
  const isAtBottomRef = useRef(true)
  const messagesRef = useRef([])
  const newMessageIds = useRef({})
  const newMessageTimeouts = useRef({})
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const videoId = useMemo(() => dataVideo?.id, [dataVideo?.id])

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (isShowChat && messageDivRef.current) {
      messageDivRef.current.scrollTop = messageDivRef.current.scrollHeight
      isAtBottomRef.current = true
    }
  }, [isShowChat])

  useEffect(() => {
    if (!isShowChat) return

    let scrollTimeout

    const handleScroll = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        if (!messageDivRef.current) return
        const { scrollTop, scrollHeight, clientHeight } = messageDivRef.current
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
        setShowScrollBtn(distanceFromBottom > 100) // Tăng threshold
        isAtBottomRef.current = distanceFromBottom <= 100 // Tăng threshold
      }, 50) // Debounce 50ms
    }

    const div = messageDivRef.current

    if (div) {
      div.addEventListener("scroll", handleScroll)
    }

    return () => {
      if (div) div.removeEventListener("scroll", handleScroll)
    }
  }, [isShowChat])

  useEffect(() => {
    if (Object.keys(userInfo).length > 0) setIsLogin(true)
  }, [userInfo])

  useEffect(() => {
    if (!videoId) return
    resetData()
    const diffTime = dataVideo?.start_time - new Date().getTime()
    const diffMinutes = diffTime / 1000 / 60
    if (diffMinutes > 30) {
      setIsAllowChat(false)
      timeEventTimeout.current = setTimeout(() => {
        setIsAllowChat(true)
      }, (diffMinutes - 30) * 60 * 1000)
    } else {
      setIsAllowChat(true)
    }
    return () => {
      if (timeInterval.current) {
        clearInterval(timeInterval.current)
        timeInterval.current = null
      }
      if (timeEventTimeout.current) {
        clearTimeout(timeEventTimeout.current)
        timeEventTimeout.current = null
      }
      if (ws.current) {
        ws.current.unsubscribe(`eventchat/${videoId}`)
        ws.current.end(true)
        ws.current = null
      }

      resetData()
    }
  }, [videoId])

  useEffect(() => {
    if (!videoId || !isAllowChat || !isLogin) return
    checkBanStatus()
    getMessageLiveChat()
    connectWebsocket(videoId)
  }, [videoId, isAllowChat])

  const checkBanStatus = async () => {
    try {
      const res = await LivechatRepository.checkBan()
      if (res?.success) {
        setStatusChat(1)
        setErrorMessage("")
      } else {
        const banMessage = res?.error_message[0]
        setModalBan({
          show: true,
          info: { message: banMessage }
        })
        setStatusChat(2)
        setErrorMessage(banMessage || "Tính năng chat bị hạn chế.")
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        logOut()
        notification.error({
          message: "Phiên đăng nhập hết hạn!"
        })
      } else {
        const errorMessage = error?.response?.data?.error_message[0] || "Đã xảy ra lỗi. Vui lòng thử lại sau!"
        setModalBan({
          show: true,
          info: { message: errorMessage }
        })
        setStatusChat(2)
        setErrorMessage(errorMessage)
      }
    }
  }

  useEffect(() => {
    if (isAtBottomRef.current && messageDivRef.current) {
      // Đợi DOM render xong
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double RAF để chắc chắn
          if (messageDivRef.current && isAtBottomRef.current) {
            messageDivRef.current.scrollTop = messageDivRef.current.scrollHeight
            // Dùng scrollTop trực tiếp thay vì scrollTo với smooth
          }
        })
      })
    }
  }, [messages])

  useEffect(() => {
    const updateVideoHeight = () => {
      const containerElement = document.querySelector("#container")
      if (containerElement) {
        const height = containerElement.offsetHeight
        if (height > 0) setVideoHeight(height)
      }
    }
    updateVideoHeight()
    const observer = new MutationObserver(updateVideoHeight)
    const container = document.querySelector("#container")?.parentElement
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"]
      })
    }
    window.addEventListener("resize", updateVideoHeight)

    return () => {
      observer.disconnect()
    }
  }, [videoId])

  async function connectWebsocket(eventID) {
    try {
      const url = process.env.NEXT_PUBLIC_SOCKET
      let clientId = userInfo.id

      const options = {
        clean: true,
        keepalive: 60,
        protocolVersion: 5,
        reconnectPeriod: 1000,
        connectTimeout: 30000,
        clientId,
        rejectUnauthorized: false,
        properties: {
          topicAliasMaximum: 0,
          sessionExpiryInterval: 0,
          receiveMaximum: 100,
          maximumPacketSize: 10000
        },
        will: {
          topic: "/will",
          payload: "dieout",
          qos: 0,
          retain: false
        }
      }

      ws.current = mqtt.connect(url, options)

      ws.current.on("connect", () => {
        console.log("✅ WebSocket connected")
        const topic = `eventchat/${eventID}`

        ws.current.subscribe(topic, { qos: 0 }, (err) => {
          if (err) console.log("Error subscribing:", err)
        })
      })

      ws.current.on("message", async (topic, payload) => {
        try {
          const data = JSON.parse(payload.toString())

          if (data?.message && data?.user_id && data?.room_id === eventID) {
            setMessages((prevMessages) => {
              // Check if message already exists to prevent duplicates
              const messageExists = prevMessages.some(
                (msg) =>
                  msg.user_id === data.user_id &&
                  msg.message === data.message &&
                  Math.abs(new Date(msg.created_at || Date.now()) - Date.now()) < 1000
              )

              if (messageExists) {
                return prevMessages
              }

              // Create new message object
              const newMessage = {
                id: `${data.user_id}-${Date.now()}`,
                user_id: data.user_id,
                user_name: data.user_name,
                avatar: data.avatar,
                message: data.message,
                raw_message: data.raw_message,
                room_id: data.room_id,
                created_at: new Date().toISOString()
              }

              const updatedMessages = [...prevMessages, newMessage]

              // Mark this message as new for animation
              newMessageIds.current[newMessage.id] = true

              // Remove the "new" flag after animation completes
              const timeoutId = setTimeout(() => {
                delete newMessageIds.current[newMessage.id]
                delete newMessageTimeouts.current[newMessage.id]
              }, 500) // Match animation duration

              newMessageTimeouts.current[newMessage.id] = timeoutId

              // Remove old messages if exceeding MAX_MESSAGES
              if (updatedMessages.length > MAX_MESSAGES) {
                console.log(`Removing ${MESSAGES_TO_REMOVE} old messages to optimize performance`)
                const removedMessages = updatedMessages.slice(0, MESSAGES_TO_REMOVE)

                // Clean up timeouts and new message flags for removed messages
                removedMessages.forEach((msg) => {
                  const timeoutId = newMessageTimeouts.current[msg.id]
                  if (timeoutId) {
                    clearTimeout(timeoutId)
                    delete newMessageTimeouts.current[msg.id]
                  }
                  delete newMessageIds.current[msg.id]
                })

                return updatedMessages.slice(MESSAGES_TO_REMOVE)
              }

              return updatedMessages
            })
          }
        } catch (error) {
          console.error("Error processing message:", error)
        }
      })

      ws.current.on("close", () => {
        console.log("WebSocket closed")
      })

      ws.current.on("error", (err) => {
        console.error("❌ WebSocket error", err)
      })

      ws.current.on("disconnect", () => {
        console.log("Disconnected from MQTT")
      })

      ws.current.on("offline", () => {
        console.log("Client went offline")
      })
    } catch (error) {
      console.error("Error establishing WebSocket connection:", error)
    }
  }

  async function getMessageLiveChat() {
    try {
      setLoading(true)

      const res = await LivechatRepository.getMessageLiveChat({
        room_id: videoId
      })

      setLoading(false)

      let newMessages = res.data || []

      // If initial messages exceed MAX_MESSAGES, only keep the most recent ones
      if (newMessages.length > MAX_MESSAGES) {
        console.log(`Initial messages (${newMessages.length}) exceed limit, keeping last ${MAX_MESSAGES}`)
        newMessages = newMessages.slice(-MAX_MESSAGES)
      }

      setMessages(newMessages)
    } catch (error) {
      console.log("Error fetching messages:", error)
      setLoading(false)
    }
  }

  const handleCloseChat = () => {
    setIsShowChat(false)
    setShowScrollBtn(false)
    if (messageDivRef.current) {
      messageDivRef.current.scrollTo({
        top: messageDivRef.current.scrollHeight,
        behavior: "smooth"
      })
    }
  }

  const handleSendComment = async (e) => {
    e?.preventDefault()
    if (!isLogin) {
      notification.warning({ message: "Vui lòng đăng nhập để bình luận" })
      return
    }
    const trimmedText = text.trim()
    if (!trimmedText) {
      notification.warning({ message: "Vui lòng nhập bình luận" })
      return
    }
    if (isSubmitting) return

    try {
      setIsSubmitting(true)
      setText("")

      await checkBanStatus()

      if (statusChat === 1) {
        const res = await LivechatRepository.sendMessageLiveChat({
          room_id: videoId,
          message: trimmedText
        })

        if (!res?.success) {
          throw new Error(res?.message || "Gửi bình luận thất bại")
        }
      }
    } catch (error) {
      console.log("Error sending comment:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const scrollToBottom = useCallback(() => {
    if (messageDivRef.current) {
      messageDivRef.current.scrollTo({
        top: messageDivRef.current.scrollHeight,
        behavior: "smooth"
      })
    }
  }, [])

  const resetData = () => {
    setMessages([])
    setStatusChat(1)
    setIsAllowChat(false)
    lastMessageCountRef.current = 0

    // Clear all animation timeouts
    Object.values(newMessageTimeouts.current).forEach((timeoutId) => clearTimeout(timeoutId))
    newMessageTimeouts.current = {}
    newMessageIds.current = {}
  }

  // Memoize the input change handler to prevent recreation on every render
  const handleTextChange = useCallback((e) => {
    setText(e.target.value)
  }, [])

  const renderUIChat = () => {
    if (dataVideo?.is_live && !isMobile) {
      return (
        <>
          {isShowChat ? (
            <div
              className="relative border border-[#5F5F5F] rounded-lg overflow-hidden mb-6"
              style={{ height: `${videoHeight}px` }}
            >
              <div className="flex items-center justify-between font-semibold pt-5 pb-3 px-4 border-b border-[#5F5F5F]">
                <div className="text-secondary">Trò chuyện</div>
                <button
                  className="w-8 h-8 flex justify-center items-center rounded-full backdrop-blur-xl border border-white/[0.04]"
                  onClick={handleCloseChat}
                >
                  {iconArrowUp}
                </button>
              </div>

              <MessageList
                messages={messages}
                loading={loading}
                messageDivRef={messageDivRef}
                newMessageIds={newMessageIds.current}
              />

              <div
                onClick={scrollToBottom}
                className={`absolute w-full text-center bottom-[82px] flex justify-center transition-opacity duration-500 ${
                  showScrollBtn ? "z-10 opacity-100" : "-z-10 opacity-0"
                }`}
              >
                <div className="bg-primary w-8 h-8 rounded-full flex items-center justify-center">{iconArrowDown}</div>
              </div>

              <ChatInput
                text={text}
                onTextChange={handleTextChange}
                onSubmit={handleSendComment}
                isLogin={isLogin}
                isAllowChat={isAllowChat}
                statusChat={statusChat}
                errorMessage={errorMessage}
                toggleModalLogin={toggleModalLogin}
              />
            </div>
          ) : (
            <div
              className="mb-6 text-[#FAFAF9] font-semibold text-center py-3 rounded-xl text-xl cursor-pointer bg-[#2E2E2E] border border-[#3A3A3A]"
              onClick={() => setIsShowChat(true)}
            >
              Hiện trò chuyện
            </div>
          )}
        </>
      )
    }
  }

  return <div>{renderUIChat()}</div>
}

export default BoxLiveChat

const iconNoChat = (
  <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M123.903 41.4447C123.903 41.4447 102.92 68.0235 69.3466 52.17C35.7731 36.3165 2.20011 79.2146 31.1101 105.327C60.0201 131.44 31.7295 145.789 51.3701 161.792C73.6929 179.981 93.1275 143.097 110.847 151.491C149.954 170.015 178.926 162.682 172.398 141.232C160.939 103.58 146.285 101.131 157.476 81.5461C168.667 61.9617 143.969 21.2408 123.903 41.4447Z"
      fill="white"
      fillOpacity="0.12"
    />
    <path
      d="M88.0537 124.839C88.0537 142.887 102.683 157.516 120.731 157.516C124.933 157.523 129.097 156.714 132.991 155.134C133.072 155.143 133.152 155.148 133.233 155.148H147.535C147.995 155.148 148.451 155.058 148.876 154.882C149.302 154.706 149.688 154.448 150.014 154.122C150.339 153.797 150.597 153.41 150.773 152.985C150.949 152.56 151.04 152.104 151.039 151.644V137.067C152.61 133.182 153.414 129.03 153.407 124.839C153.407 106.791 138.779 92.1623 120.731 92.1623C102.683 92.1623 88.0537 106.791 88.0537 124.839Z"
      fill="#B8D9FF"
    />
    <path
      d="M133.81 101.677C133.81 124.319 115.458 142.672 92.8156 142.672C87.5432 142.68 82.3192 141.665 77.4337 139.683C77.3331 139.695 77.2319 139.701 77.1307 139.701H59.188C58.6105 139.701 58.0387 139.588 57.5051 139.367C56.9716 139.146 56.4868 138.822 56.0785 138.414C55.6701 138.006 55.3463 137.521 55.1255 136.987C54.9046 136.454 54.7911 135.882 54.7915 135.304V117.017C52.8212 112.143 51.8125 106.934 51.8209 101.677C51.8209 79.0353 70.1733 60.6829 92.8153 60.6829C115.457 60.6829 133.81 79.035 133.81 101.677Z"
      fill="#1877F2"
    />
    <path
      d="M73.8035 109.995C77.0847 109.995 79.7447 107.335 79.7447 104.053C79.7447 100.772 77.0847 98.1122 73.8035 98.1122C70.5223 98.1122 67.8623 100.772 67.8623 104.053C67.8623 107.335 70.5223 109.995 73.8035 109.995Z"
      fill="white"
    />
    <path
      d="M91.0325 109.995C94.3137 109.995 96.9737 107.335 96.9737 104.053C96.9737 100.772 94.3137 98.1122 91.0325 98.1122C87.7513 98.1122 85.0913 100.772 85.0913 104.053C85.0913 107.335 87.7513 109.995 91.0325 109.995Z"
      fill="white"
    />
    <path
      d="M108.857 109.995C112.138 109.995 114.798 107.335 114.798 104.053C114.798 100.772 112.138 98.1122 108.857 98.1122C105.575 98.1122 102.916 100.772 102.916 104.053C102.916 107.335 105.575 109.995 108.857 109.995Z"
      fill="white"
    />
    <path
      d="M127.274 82.0708C133.181 82.0708 137.968 77.2829 137.968 71.3766C137.968 65.4704 133.181 60.6824 127.274 60.6824C121.368 60.6824 116.58 65.4704 116.58 71.3766C116.58 77.2829 121.368 82.0708 127.274 82.0708Z"
      fill="#1877F2"
      stroke="white"
      strokeWidth="15"
      strokeMiterlimit="10"
    />
    <path
      d="M130.673 68.207C130.532 67.6632 130.28 67.1547 129.931 66.714C129.624 66.3428 129.225 66.0581 128.774 65.8879C127.801 65.5522 126.743 65.5522 125.77 65.8879C125.32 66.0571 124.922 66.342 124.617 66.714C124.265 67.1526 124.012 67.6618 123.875 68.207C123.688 68.9414 123.6 69.6977 123.614 70.4554V72.2577C123.599 73.0173 123.688 73.7754 123.88 74.5106C124.018 75.0602 124.273 75.5738 124.626 76.017C124.933 76.3919 125.332 76.6811 125.783 76.8563C126.752 77.2037 127.81 77.2037 128.778 76.8563C129.228 76.6809 129.623 76.3916 129.927 76.017C130.275 75.5715 130.526 75.0585 130.665 74.5106C130.852 73.7747 130.939 73.017 130.925 72.2577H130.934V70.4554C130.949 69.6977 130.861 68.9414 130.673 68.207ZM129.061 72.527C129.067 73.0081 129.029 73.4888 128.946 73.9628C128.89 74.2998 128.775 74.6244 128.606 74.9212C128.476 75.1503 128.283 75.3373 128.05 75.4602C127.81 75.5755 127.547 75.6331 127.281 75.628C127.015 75.632 126.752 75.5746 126.512 75.4602C126.276 75.3367 126.079 75.1501 125.942 74.9212C125.77 74.6248 125.65 74.3004 125.589 73.9628C125.499 73.4897 125.457 73.0086 125.465 72.527V70.1683C125.458 69.6885 125.498 69.2091 125.585 68.7371C125.643 68.4063 125.761 68.0889 125.934 67.8006C126.07 67.5804 126.266 67.4029 126.499 67.2882C126.74 67.1834 127 67.1292 127.263 67.1292C127.526 67.1292 127.786 67.1834 128.027 67.2882C128.262 67.4001 128.458 67.5783 128.593 67.8006C128.765 68.0889 128.884 68.4063 128.942 68.7371C129.029 69.2091 129.069 69.6885 129.061 70.1683V72.5272L129.061 72.527Z"
      fill="white"
    />
  </svg>
)

const iconArrowUp = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_7883_166438)">
      <path d="M6 14L12 8L18 14" stroke="#D6D6D6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_7883_166438">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const iconSend = (
  <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.79005 3.27203L24.86 13.35C25.0727 13.4518 25.2523 13.6117 25.378 13.8112C25.5037 14.0107 25.5704 14.2417 25.5704 14.4775C25.5704 14.7133 25.5037 14.9443 25.378 15.1438C25.2523 15.3433 25.0727 15.5032 24.86 15.605L3.79005 25.683C3.57396 25.7866 3.33266 25.8259 3.09488 25.7962C2.8571 25.7665 2.63286 25.6691 2.44888 25.5156C2.26489 25.362 2.12892 25.1589 2.05714 24.9302C1.98537 24.7016 1.98082 24.4572 2.04405 24.226L4.15205 16.498C4.17785 16.4033 4.23096 16.3183 4.30476 16.2536C4.37856 16.1888 4.46978 16.1473 4.56705 16.134L14.777 14.747C14.8196 14.7411 14.8599 14.7242 14.8941 14.6981C14.9282 14.672 14.9551 14.6375 14.972 14.598L14.99 14.535C14.9979 14.4792 14.9867 14.4223 14.9582 14.3737C14.9297 14.325 14.8856 14.2875 14.833 14.267L14.778 14.252L4.57805 12.866C4.48096 12.8526 4.38996 12.811 4.31635 12.7462C4.24275 12.6815 4.18978 12.5966 4.16405 12.502L2.04405 4.72903C1.98082 4.49789 1.98537 4.25345 2.05714 4.02483C2.12892 3.7962 2.26489 3.59303 2.44888 3.4395C2.63286 3.28597 2.8571 3.18855 3.09488 3.15886C3.33266 3.12917 3.57396 3.16845 3.79005 3.27203Z"
      fill="#1877F2"
    />
  </svg>
)

const iconArrowDown = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_7883_166429)">
      <path d="M6 10L12 16L18 10" stroke="#FAFAF9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_7883_166429">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
)
